import { combineReducers } from "redux";
import { type Duck } from "@focus/interfaces";
import { type PayloadAction } from "@reduxjs/toolkit";
import { ItemStatus } from "@focus/focusUI";

export type DucksMap = {
  [key: string]: Duck;
};

export function createReducer<Ducks extends DucksMap>(ducks: Ducks) {
  const reducers = Object.fromEntries(
    Object.entries(ducks)
      .filter(([, duck]) => typeof duck.reducer === "function")
      .map(([key, duck]) => [key, duck.reducer]),
  ) as {
    [K in keyof Ducks]: Ducks[K]["reducer"];
  };

  return combineReducers(reducers);
}

export function getMiddlewares(duck: Duck) {
  if (!duck.middleware) return [];
  return Object.values(duck.middleware);
}

export type SuccessNotificationMeta = {
  notificationOnSuccess?: string;
};

export type FailureNotificationMeta = {
  errorMessage?: string;
};

type ErrorPayload = string | { message?: string } | null | undefined;

interface RequestItem {
  status: ItemStatus;
  error: string | null;
  fieldErrors?: Record<string, string> | null;
}

const getErrorMessage = (error: ErrorPayload) => {
  if (typeof error === "string") return error;
  return error?.message ?? null;
};

const getFieldErrors = (error: ErrorPayload) => {
  if (!error || typeof error === "string" || !("errors" in error)) return null;
  return error.errors && typeof error.errors === "object"
    ? (error.errors as Record<string, string>)
    : null;
};

export const createAsyncReducers = <
  State extends { requests: Record<Key, RequestItem> },
  Key extends keyof State["requests"],
  SuccessPayload,
  RequestPayload = void,
  FailurePayload extends ErrorPayload = string,
>(
  key: Key,
  onSuccess: (
    state: State,
    action: PayloadAction<SuccessPayload, string, any>,
  ) => void,
) => ({
  request: (state: State, _action: PayloadAction<RequestPayload>) => {
    state.requests[key].status = ItemStatus.PENDING;
    state.requests[key].error = null;
    state.requests[key].fieldErrors = null;
  },
  success: {
    reducer: (
      state: State,
      action: PayloadAction<SuccessPayload, string, any>,
    ) => {
      state.requests[key].status = ItemStatus.READY;
      state.requests[key].fieldErrors = null;
      onSuccess(state, action);
    },
    prepare: (payload: SuccessPayload, meta?: any) => ({
      payload,
      meta,
    }),
  },
  failure: {
    reducer: (
      state: State,
      action: PayloadAction<FailurePayload, string, FailureNotificationMeta>,
    ) => {
      state.requests[key].status = ItemStatus.ERROR;
      state.requests[key].error = getErrorMessage(action.payload);
      state.requests[key].fieldErrors = getFieldErrors(action.payload);
    },
    prepare: (payload: FailurePayload, meta?: FailureNotificationMeta) => ({
      payload,
      meta,
    }),
  },
});
